import { useState } from "react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { tournaments } from "../../lib/tournamentApi";
import { Pill, fmtSlot } from "./shared";

const STATUS = {
  pending: ["muted", "Pending"], checked_in: ["green", "Checked in"], no_show: ["red", "No-show"],
};

export default function CheckinTab({ data, division, onChanged }) {
  const t = data.tournament;
  const teams = data.teams.filter((x) => x.division_id === division.id);
  const [prompt, setPrompt] = useState(null);
  const counts = teams.reduce((acc, x) => ({ ...acc, [x.checkin_status]: (acc[x.checkin_status] || 0) + 1 }), {});

  async function set(team, status) {
    try {
      const d = await tournaments.checkin(t.id, team.id, status);
      await onChanged();
      if (status === "no_show" && d.next_game) setPrompt({ team, game: d.next_game });
      else toast.success(`${team.name}: ${STATUS[status][1]}`);
    } catch (e) { toast.error(e.message || "Couldn't update."); }
  }

  return (
    <div data-testid="checkin-tab">
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }} data-testid="checkin-summary">
        <Pill tone="green">{counts.checked_in || 0} here</Pill>
        <Pill>{counts.pending || 0} pending</Pill>
        <Pill tone="red">{counts.no_show || 0} no-show</Pill>
      </div>
      <SectionTitle label="Check-in board" />
      {teams.map((team) => {
        const [tone, label] = STATUS[team.checkin_status] || STATUS.pending;
        return (
          <ChalkCard key={team.id} data-testid={`checkin-row-${team.id}`} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, fontWeight: 600 }}>{team.name}</div>
              <Pill tone={tone} testId={`checkin-status-${team.id}`}>{label}</Pill>
            </div>
            {team.checked_in_at && team.checkin_status === "checked_in" && (
              <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 2 }}>at {fmtSlot(team.checked_in_at, t.timezone)}</div>
            )}
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              {team.checkin_status !== "checked_in" && (
                <ChalkActionButton label="Check in" fullWidth={false} testId={`checkin-in-${team.id}`} onClick={() => set(team, "checked_in")} />
              )}
              {team.checkin_status !== "no_show" && (
                <ChalkActionButton label="No-show" variant="ghost" fullWidth={false} testId={`checkin-noshow-${team.id}`} onClick={() => set(team, "no_show")} />
              )}
              {team.checkin_status !== "pending" && (
                <ChalkActionButton label="Reset" variant="ghost" fullWidth={false} testId={`checkin-reset-${team.id}`} onClick={() => set(team, "pending")} />
              )}
            </div>
          </ChalkCard>
        );
      })}
      {prompt && <ForfeitPrompt t={t} prompt={prompt} onClose={() => setPrompt(null)} onChanged={onChanged} />}
    </div>
  );
}

function ForfeitPrompt({ t, prompt, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const { team, game } = prompt;
  async function forfeit() {
    setBusy(true);
    try {
      await tournaments.forfeitNext(t.id, team.id);
      toast.success(`${team.name} forfeited. ${game.opponent_name || "Opponent"} advances.`);
      await onChanged();
      onClose();
    } catch (e) { toast.error(e.message || "Couldn't forfeit."); }
    finally { setBusy(false); }
  }
  return (
    <BottomSheet isOpen title="Forfeit next game?" onDismiss={onClose}>
      <div data-testid="forfeit-prompt" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          <b>{team.name}</b> is marked No-show. Their next game is vs <b>{game.opponent_name || "TBD"}</b>
          {game.scheduled_at ? ` (${[game.court, fmtSlot(game.scheduled_at, t.timezone)].filter(Boolean).join(" · ")})` : ""}.
        </div>
        {game.opponent_name ? (
          <ChalkActionButton variant="danger" label={`Forfeit — ${game.opponent_name} advances`} loading={busy}
                             testId="forfeit-confirm" onClick={forfeit} />
        ) : (
          <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>The opponent isn't set yet — forfeit once the matchup is known.</div>
        )}
        <ChalkActionButton variant="ghost" label="Not now" testId="forfeit-cancel" onClick={onClose} />
      </div>
    </BottomSheet>
  );
}
